import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { MatchedBudget } from "./PdfOfferUpload";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";

interface OfferPickerProps {
  offers: MatchedBudget[];
  onSelect: (offer: MatchedBudget) => void;
  /** Klikket på "se kildefil" i en rad — foreldre håndterer FilePreviewDialog. */
  onPreview?: (offerIdx: number) => void;
}

/**
 * Multi-select picker. Bruker huker av ett eller flere budsjetter.
 *   • Ett valgt → bruk det direkte.
 *   • Flere valgt → kombiner alle linjer til ETT samlet budsjett (typisk
 *     hovedtilbud + opsjon kunden valgte begge). offer_total/overhead/rabatt
 *     summeres, og budsjettets navn settes til "Kombinert: A + B".
 */
export function OfferPicker({ offers, onSelect, onPreview }: OfferPickerProps) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // VIB-1021: per-budsjett tilvalg-overstyring. Default = Vision-detektert
  // is_optional; PL kan overstyre per budsjett før kombinering.
  const [optionalOverride, setOptionalOverride] = useState<Record<number, boolean>>({});
  const isOptional = (idx: number) => optionalOverride[idx] ?? offers[idx]?.is_optional ?? false;

  const toggle = (idx: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleOptional = (idx: number) =>
    setOptionalOverride(prev => ({ ...prev, [idx]: !isOptional(idx) }));

  const combineSelected = (): MatchedBudget => {
    const pickedIdx = offers.map((_, i) => i).filter(i => selected.has(i));
    const picked = pickedIdx.map(i => ({ offer: offers[i], optional: isOptional(i) }));

    // Stempel hver linje med kildebudsjettets tilvalg-flagg, slik at det
    // overlever flat-kombineringen (mal-/cost_type-tilknytning bevares uendret).
    const stamp = (p: { offer: MatchedBudget; optional: boolean }) =>
      p.offer.lines.map((l) => ({
        ...l,
        is_optional: p.optional,
        // VIB-1142: bær kilde-budsjettets navn slik at hvert tilvalg-budsjett
        // blir sin egen seksjon i import-hooken (ikke samlet til én «Tilvalg»).
        option_group: p.optional ? p.offer.name : null,
      }));

    if (picked.length === 1) {
      const p = picked[0];
      return { ...p.offer, is_optional: p.optional, lines: stamp(p) };
    }

    const allLines = picked.flatMap(stamp);
    const totalAmount = picked.reduce((s, p) => s + p.offer.offer_total, 0);
    // Vektet snitt for overhead — hver budsjett bidrar i forhold til sin
    // bunnlinje-sum. Hvis alle har samme overhead_pct blir det det samme.
    const linesSums = picked.map(p =>
      p.offer.lines.reduce((s, l) => s + l.amount, 0),
    );
    const totalLineSum = linesSums.reduce((s, x) => s + x, 0);
    const overheadPct = totalLineSum > 0
      ? picked.reduce(
          (s, p, i) => s + p.offer.overhead_pct * (linesSums[i] / totalLineSum),
          0,
        )
      : picked[0].offer.overhead_pct;
    const discountAmount = picked.reduce((s, p) => s + p.offer.discount_amount, 0);

    return {
      ...picked[0].offer,
      name: `Kombinert: ${picked.map(p => p.offer.name).join(" + ")}`,
      // Budsjett-nivå flagg er sant kun hvis alle kombinerte budsjetter er
      // tilvalg; per-linje-flagget over er den autoritative kilden videre.
      is_optional: picked.every(p => p.optional),
      lines: allLines,
      offer_total: totalAmount,
      overhead_pct: Math.round(overheadPct * 10) / 10,
      discount_pct: 0,
      discount_amount: discountAmount,
      validation_diff: 0,
      unmatched_count: picked.reduce((s, p) => s + p.offer.unmatched_count, 0),
    };
  };

  const handleConfirm = () => {
    if (selected.size === 0) return;
    onSelect(combineSelected());
  };

  const totalSelected = Array.from(selected).reduce(
    (s, i) => s + (offers[i]?.offer_total ?? 0),
    0,
  );

  return (
    <div className="p-4 border rounded-lg bg-amber-50 space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="min-w-0 text-sm font-medium">
          {offers.length} tilbud funnet. Huk av det/de du vil importere — linjer
          merket «Tilvalg» legges som egne seksjoner i tilbudet.
        </p>
        <Button
          size="sm"
          onClick={handleConfirm}
          disabled={selected.size === 0}
          className="shrink-0 self-start whitespace-nowrap sm:self-auto"
        >
          {selected.size === 0
            ? "Importer valgte"
            : `Importer ${selected.size} valgte (${formatNOK(totalSelected)})`}
        </Button>
      </div>
      <div className="space-y-2">
        {offers.map((offer, i) => {
          const isOn = selected.has(i);
          return (
            <label
              key={i}
              className={`flex items-center gap-3 p-3 rounded-lg border bg-white cursor-pointer transition-colors ${
                isOn ? "border-primary bg-primary/5" : "hover:bg-muted/30"
              }`}
            >
              <Checkbox checked={isOn} onCheckedChange={() => toggle(i)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{offer.name}</p>
                <p className="text-xs text-muted-foreground">
                  {offer.lines.length} budsjettposter
                  {offer.unmatched_count > 0 && (
                    <span className="ml-1 text-yellow-700">
                      · {offer.unmatched_count} uten mal-match
                    </span>
                  )}
                </p>
              </div>
              <span className="text-sm font-medium tabular-nums">
                {formatNOK(offer.offer_total)}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleOptional(i);
                }}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-colors ${
                  isOptional(i)
                    ? "bg-amber-100 text-amber-800 border-amber-300"
                    : "bg-white text-muted-foreground border-border hover:bg-muted/50"
                }`}
                title="Marker budsjettet som tilvalg/opsjon"
                aria-pressed={isOptional(i)}
              >
                {isOptional(i) && <SrIcon name="check" className="w-3 h-3" />}
                Tilvalg
              </button>
              {onPreview && offer._sourceFileIdx != null && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onPreview(i);
                  }}
                  className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"
                  title="Se kildefil"
                  aria-label="Se kildefil"
                >
                  <SrIcon name="eye" className="w-4 h-4" />
                </button>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}
